import React from 'react'
import {
  Box,
  Card,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Link } from '../../../link'
import {
  computeBoardScorecard,
  computeFanoutHealthSummary,
  formatDelayMs,
  formatRate
} from './boardAckDelayStats'
import type { ParentFanoutRecord } from './types'

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(1)
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  summaryItem: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50]
  },
  summaryValue: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 600,
    lineHeight: 1.2
  },
  summaryLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary
  },
  compactCell: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12)
  },
  flagCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5)
  },
  flagChip: {
    height: 20,
    fontSize: theme.typography.pxToRem(10)
  },
  scorecardHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 9999,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[50],
    cursor: 'pointer',
    userSelect: 'none',
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.grey[400]
    },
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
    }
  },
  scorecardTitle: {
    flex: 1
  },
  scorecardMeta: {
    marginRight: theme.spacing(0.5)
  },
  scorecardToggleIcon: {
    display: 'flex',
    color: theme.palette.text.secondary
  }
}))

const SummaryMetric: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const classes = useStyles()
  return (
    <Box className={classes.summaryItem}>
      <Typography className={classes.summaryValue}>{value}</Typography>
      <Typography className={classes.summaryLabel}>{label}</Typography>
    </Box>
  )
}

export const FanoutStatsPanel: React.FC<{
  records: ParentFanoutRecord[]
}> = ({ records }) => {
  const classes = useStyles()
  const [scorecardExpanded, setScorecardExpanded] = React.useState(false)
  const health = React.useMemo(() => computeFanoutHealthSummary(records), [records])
  const scorecard = React.useMemo(() => computeBoardScorecard(records), [records])

  if (health.evaluableBoardSlots === 0 && scorecard.length === 0) return null

  return (
    <Card className={classes.card} variant='outlined'>
      <Typography variant='subtitle2' className={classes.title}>
        Fanout health summary
      </Typography>
      <Typography variant='caption' color='textSecondary' display='block' gutterBottom>
        Based on {health.loadedParentCount} loaded parent appearance{health.loadedParentCount === 1 ? '' : 's'}.
        Appear rate and missing counts exclude content-unchanged slots and the oldest loaded parent; ack
        rate is among appearances only. Load more history to refine these metrics.
      </Typography>

      <Box className={classes.summaryGrid}>
        <SummaryMetric label='Appeared' value={formatRate(health.appearedRate)} />
        <SummaryMetric label='Acked' value={formatRate(health.ackedRate)} />
        <SummaryMetric label='Content unchanged' value={formatRate(health.contentUnchangedRate)} />
        <SummaryMetric label='Unexplained missing' value={formatRate(health.unexplainedMissingRate)} />
        <SummaryMetric
          label='P50 ack delay'
          value={health.p50AckDelayMs == null ? '—' : formatDelayMs(health.p50AckDelayMs)}
        />
        <SummaryMetric
          label='P95 ack delay'
          value={health.p95AckDelayMs == null ? '—' : formatDelayMs(health.p95AckDelayMs)}
        />
      </Box>

      {scorecard.length > 0 ? (
        <>
          <Box
            className={classes.scorecardHeader}
            onClick={() => setScorecardExpanded(expanded => !expanded)}
            role='button'
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setScorecardExpanded(expanded => !expanded)
              }
            }}
            aria-expanded={scorecardExpanded}
          >
            <Typography variant='subtitle2' className={classes.scorecardTitle}>
              Board scorecard
            </Typography>
            <Typography variant='caption' color='textSecondary' className={classes.scorecardMeta}>
              {scorecard.length} board{scorecard.length === 1 ? '' : 's'}
            </Typography>
            <Box className={classes.scorecardToggleIcon} aria-hidden>
              {scorecardExpanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
            </Box>
          </Box>
          <Collapse in={scorecardExpanded}>
            <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell className={classes.compactCell}>Board</TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Appear
                </TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Ack
                </TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Unchanged
                </TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Missing
                </TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Avg delay
                </TableCell>
                <TableCell className={classes.compactCell} align='right'>
                  Median
                </TableCell>
                <TableCell className={classes.compactCell}>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scorecard.map(row => (
                <TableRow key={row.boardId}>
                  <TableCell className={classes.compactCell}>
                    <Link to={`/boards/${row.boardId}`}>{row.boardTitle || row.boardId}</Link>
                    <Typography variant='caption' color='textSecondary' display='block'>
                      {row.parentSamples} sample{row.parentSamples === 1 ? '' : 's'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {formatRate(row.appearedRate)}
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {formatRate(row.ackedRate)}
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {formatRate(row.contentUnchangedRate)}
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {row.unexplainedMissingCount}
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {row.averageDelayMs == null ? '—' : formatDelayMs(row.averageDelayMs)}
                  </TableCell>
                  <TableCell className={classes.compactCell} align='right'>
                    {row.medianDelayMs == null ? '—' : formatDelayMs(row.medianDelayMs)}
                  </TableCell>
                  <TableCell className={classes.compactCell}>
                    <Box className={classes.flagCell}>
                      {row.flags.slowAck ? (
                        <Chip label='Slow ack' size='small' color='secondary' className={classes.flagChip} />
                      ) : null}
                      {row.flags.lowAppearRate ? (
                        <Chip label='Low appear' size='small' className={classes.flagChip} />
                      ) : null}
                      {row.flags.lowAckRate ? (
                        <Chip label='Low ack' size='small' className={classes.flagChip} />
                      ) : null}
                      {!row.flags.slowAck && !row.flags.lowAppearRate && !row.flags.lowAckRate ? (
                        <Typography variant='caption' color='textSecondary'>
                          —
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Collapse>
        </>
      ) : null}
    </Card>
  )
}
